// @ts-check
/**
 * Auth context - typed wrapper around the existing `window.ClubHQAuth` global
 * published by `Frontend/auth.js`. Lets new ES-module code import a clean
 * interface while legacy non-module pages keep working through the global.
 *
 * During the migration both layers stay live. Once every page is module-based
 * we can move the implementation in here and drop the IIFE shim.
 */

const TOKEN_KEY = "clubhq:token";
const CLUB_KEY = "clubhq:clubCode";
const EMAIL_KEY = "clubhq:email";
const FULLNAME_KEY = "clubhq:fullName";
const ROLE_KEY = "clubhq:role";
const PERMS_KEY = "clubhq:permissions";
const MEMBER_ID_KEY = "clubhq:memberId";

/** @returns {string} */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

/** @returns {string} */
export function getClubCode() {
  return (localStorage.getItem(CLUB_KEY) || "").trim().toUpperCase();
}

/** @returns {string} */
export function getEmail() {
  return (localStorage.getItem(EMAIL_KEY) || "").toLowerCase();
}

/** @returns {string} */
export function getFullName() {
  return localStorage.getItem(FULLNAME_KEY) || "";
}

/** @returns {string} */
export function getRole() {
  return localStorage.getItem(ROLE_KEY) || "";
}

/** @returns {string} */
export function getMemberId() {
  return localStorage.getItem(MEMBER_ID_KEY) || "";
}

/**
 * @returns {{ permissions?: string[] | null } | null}
 */
export function getPermissions() {
  const raw = localStorage.getItem(PERMS_KEY);
  if (!raw || raw === "null") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Returns the standard `Authorization` header pair, or empty object when
 * no token is stored.
 * @returns {Record<string, string>}
 */
export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Wipe local auth state and bounce to the login page.
 */
export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CLUB_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(FULLNAME_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(PERMS_KEY);
  localStorage.removeItem(MEMBER_ID_KEY);
  window.location.href = "/";
}

/**
 * Redirect to login when no token is present.
 * @returns {boolean} true when authenticated, false (and redirected) otherwise.
 */
export function requireAuth() {
  if (!getToken()) {
    window.location.href = "/";
    return false;
  }
  return true;
}

/**
 * Convenience snapshot for callers that want one bundle (e.g. building API
 * query strings).
 * @returns {{ clubCode: string, email: string, role: string, memberId: string, fullName: string }}
 */
export function getAuthContext() {
  return {
    clubCode: getClubCode(),
    email: getEmail(),
    role: getRole(),
    memberId: getMemberId(),
    fullName: getFullName(),
  };
}
